import * as signalR from "@microsoft/signalr";
import { RefObject, useCallback, useMemo, useRef, useState } from "react";

interface DroneHubHook {
    connection: signalR.HubConnection;
    protocol: RefObject<signalR.JsonHubProtocol>;
    start: () => void;
    stop: () => void;
}

/**
 * Одно соединение с хабом на всё приложение.
 *
 * Раньше каждый вызов useDroneHubConnection() строил собственное. Точек вызова
 * одиннадцать: на загрузке консоли открывалось столько же соединений, плюс ещё
 * три на каждый выбор дрона. Если WebSocket-апгрейд не проходит (а на стенде он
 * не проходит), SignalR откатывается на long polling — и каждое из полутора
 * десятков соединений начинает непрерывно переспрашивать сервер. Отсюда и шквал
 * запросов к /api/drone-hub.
 *
 * Теперь соединение ровно одно, а подписки компонентов живут на нём
 * параллельно: каждый вешает свои обработчики в useEffect и снимает их в
 * cleanup через connection.off(). Поэтому отписка здесь — несущая конструкция,
 * а не подстраховка: без неё обработчики ушедших компонентов остались бы
 * висеть на общем соединении.
 */
const protocol = new signalR.JsonHubProtocol();

// Пауза перед повторной попыткой первого подключения.
const START_RETRY_MS = [1000, 2000, 5000, 10000, 30000];

let sharedConnection: signalR.HubConnection | null = null;

/**
 * withAutomaticReconnect поднимает только УЖЕ установленное соединение, а
 * неудачный первый start не повторяет. Пока соединений было полтора десятка,
 * какое-нибудь да поднималось, и это не замечалось; с единственным промах на
 * старте оставил бы консоль без телеметрии совсем.
 */
function startWithRetry(connection: signalR.HubConnection, attempt = 0): void {
    if (connection.state !== signalR.HubConnectionState.Disconnected) {
        return;
    }

    connection.start().catch(() => {
        const delay = START_RETRY_MS[Math.min(attempt, START_RETRY_MS.length - 1)] ?? 30000;
        window.setTimeout(() => {
            // За время паузы соединение могли закрыть насовсем (выход из системы).
            if (sharedConnection === connection) {
                startWithRetry(connection, attempt + 1);
            }
        }, delay);
    });
}

function getConnection(): signalR.HubConnection {
    if (sharedConnection) {
        return sharedConnection;
    }

    // Адрес абсолютный: на вложенных роутах (/statistics/modem) относительный ушёл бы мимо /api.
    // Токен хабу не передаём: куку hawk.sid браузер сам цепляет и к negotiate, и к WebSocket-рукопожатию.
    const connection = new signalR.HubConnectionBuilder()
        .withUrl("/api/drone-hub")
        .withAutomaticReconnect([0, 2000, 10000, 10000, 30000, 10000])
        .withHubProtocol(protocol)
        .build();

    sharedConnection = connection;
    startWithRetry(connection);

    return connection;
}

/**
 * Закрывает общее соединение. Вызывается при выходе из системы: куки больше
 * нет, а иначе withAutomaticReconnect будет вечно долбиться в хаб от имени
 * несуществующей сессии. Следующий вызов хука поднимет соединение заново.
 */
export function stopDroneHub(): void {
    if (sharedConnection) {
        sharedConnection.stop();
        sharedConnection = null;
    }
}

function useDroneHubConnection(): DroneHubHook {
    const protocolRef = useRef<signalR.JsonHubProtocol>(protocol);
    const [connection] = useState<signalR.HubConnection>(getConnection);

    // Размонтирование компонента соединение НЕ закрывает: оно общее, и остальные
    // подписчики продолжают им пользоваться. Рвёт его только stopDroneHub().

    const start = useCallback((): void => {
        startWithRetry(connection);
    }, [connection]);

    const stop = useCallback((): void => {
        connection.stop();
    }, [connection]);

    // Объект обязан быть стабильным: подписчики кладут его в зависимости
    // useEffect. Пока отсюда возвращался новый литерал на каждый рендер, их
    // эффекты перезапускались после каждой перерисовки и вешали на хаб ещё
    // один обработчик, не снимая прежние.
    return useMemo(() => ({
        protocol: protocolRef,
        connection,
        start,
        stop
    }), [connection, start, stop]);
}

export default useDroneHubConnection;
